import { prefersReducedMotion } from "@neucleos/motion-primitives";
import type { ColorReader } from "@neucleos/theme/metalforge";

/**
 * The app boundary for the frozen `@neucleos/metalforge-web` catalog: the
 * generated components render through WebGPU and animate on their own
 * requestAnimationFrame loop, so the app decides, once per mount, whether one
 * may move at all. Motion is information, never decoration: when it cannot
 * move, the caller keeps its still signal.
 */
export function metalforgeCanMove(): boolean {
  return webgpuAvailable() && metalforgeMotionAllowed();
}

/** The GPU the generated shaders need; the CSS loaders do not. */
export function webgpuAvailable(): boolean {
  if (typeof navigator === "undefined") return false;
  return Boolean((navigator as { gpu?: unknown }).gpu);
}

/** Whether anything ambient may move at all: reduced motion and the ambient-motion switch still every kind (ST10). */
export function metalforgeMotionAllowed(): boolean {
  if (typeof document === "undefined") return false;
  if (prefersReducedMotion()) return false;
  if (document.documentElement.dataset["ambientMotion"] === "off") return false;
  return true;
}

/**
 * Reads one CSS color value as the browser resolves it at `host`, normalized
 * to `rgb(r, g, b)` through a 1×1 canvas so oklch and color() values (the
 * theme's World accents) become sRGB bytes the shader can take. Returns null
 * on a stylesheet-less host (tests, SSR) so callers keep their still fallback.
 */
export function createThemeColorReader(host: HTMLElement | null): ColorReader {
  return (cssColor) => {
    if (typeof document === "undefined") return null;
    const probe = document.createElement("span");
    probe.style.color = cssColor;
    (host ?? document.body).appendChild(probe);
    let computed = "";
    try {
      computed = getComputedStyle(probe).color;
    } catch {
      computed = "";
    } finally {
      probe.remove();
    }
    if (!computed) return null;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      const context = canvas.getContext("2d");
      if (!context) return computed;
      context.fillStyle = "#000000";
      context.fillStyle = computed;
      context.fillRect(0, 0, 1, 1);
      const [r, g, b] = context.getImageData(0, 0, 1, 1).data;
      return `rgb(${r}, ${g}, ${b})`;
    } catch {
      return computed;
    }
  };
}
